import { useTran } from "@/hooks";
import { Label } from "@/components/ui/label";
import { YearMonthPicker } from "@/components/common";
import { AlertTriangle, CalendarCheck2Icon } from "lucide-react";

const currentYear = new Date().getFullYear();
const END_MAX_YEAR = currentYear + 10;

const padMonth = (month) => String(month).padStart(2, "0");

const getLastDayOfMonth = (year, month) => new Date(year, month, 0).getDate();

const toStartDate = (year, month) =>
  `${year}-${padMonth(month)}-01T00:00:00.000Z`;

const toEndDate = (year, month) =>
  `${year}-${padMonth(month)}-${getLastDayOfMonth(year, month)}T00:00:00.000Z`;

const addMonths = (year, month, monthsToAdd) => {
  const monthIndex = month - 1 + monthsToAdd;

  return {
    year: year + Math.floor(monthIndex / 12),
    month: (monthIndex % 12) + 1,
  };
};

const getInclusiveMonthCount = (startYear, startMonth, endYear, endMonth) => {
  if (!startYear || !startMonth || !endYear || !endMonth) return 0;
  return (endYear - startYear) * 12 + endMonth - startMonth + 1;
};

const StepSelectYear = ({
  formData,
  updateFormData,
  disableYearSelection,
  validationMessage,
}) => {
  const { t } = useTran();

  const setReportingPeriod = ({ startYear, startMonth, endYear, endMonth }) => {
    updateFormData("startYear", startYear);
    updateFormData("startMonth", startMonth);
    updateFormData("endYear", endYear);
    updateFormData("endMonth", endMonth);

    updateFormData("startDate", toStartDate(startYear, startMonth));
    updateFormData("endDate", toEndDate(endYear, endMonth));
  };

  const handleStartYearMonthChange = ({ year, month }) => {
    const end = addMonths(year, month, 11);

    setReportingPeriod({
      startYear: year,
      startMonth: month,
      endYear: end.year,
      endMonth: end.month,
    });
  };

  const formatYearMonth = (year, month) => {
    if (!year || !month) return "-";

    return new Date(year, month - 1).toLocaleString("en", {
      month: "long",
      year: "numeric",
    });
  };

  const durationMonths = getInclusiveMonthCount(
    formData.startYear,
    formData.startMonth,
    formData.endYear,
    formData.endMonth,
  );

  return (
    <div className="rounded-radius border border-border bg-card">
      <div className="border-b border-border p-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-radius bg-primary/10 text-primary">
            <CalendarCheck2Icon className="h-5 w-5" />
          </div>

          <div className="min-w-0">
            <h2 className="font-bold text-primary">
              {t("Select Reporting Period")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t(
                "Choose the start year and month. The end period will be calculated automatically for 12 months.",
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t("Start Year & Month")}
              </Label>

              <YearMonthPicker
                value={
                  formData.startYear
                    ? {
                        year: formData.startYear,
                        month: formData.startMonth,
                      }
                    : null
                }
                onChange={handleStartYearMonthChange}
                placeholder={t("Select start year & month")}
                minYear={2018}
                maxYear={END_MAX_YEAR}
                disabled={disableYearSelection}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium">
                {t("End Year & Month")}
              </Label>

              <YearMonthPicker
                value={
                  formData.endYear
                    ? {
                        year: formData.endYear,
                        month: formData.endMonth,
                      }
                    : null
                }
                onChange={() => {}}
                placeholder={t("Auto calculated end year & month")}
                disabled
                minYear={2018}
                maxYear={END_MAX_YEAR}
              />

              <p className="text-xs text-muted-foreground">
                {t("End period is automatically calculated as 12 months.")}
              </p>
            </div>
          </div>

          <div>
            <Label className="text-sm font-medium">
              {t("Reporting Period Summary")}
            </Label>

            <div className="mt-2 space-y-2 rounded-radius border border-border bg-muted/20 p-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {t("Start Date")}
                </span>
                <span className="text-sm font-medium">
                  {formatYearMonth(formData.startYear, formData.startMonth)}
                </span>
              </div>

              <div className="h-px bg-border" />

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {t("End Date")}
                </span>
                <span className="text-sm font-medium">
                  {formatYearMonth(formData.endYear, formData.endMonth)}
                </span>
              </div>

              <div className="h-px bg-border" />

              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {t("Duration")}
                </span>
                <span className="text-sm font-medium">
                  {durationMonths > 0 ? durationMonths : "-"} {t("months")}
                </span>
              </div>
            </div>
          </div>
        </div>

        {validationMessage ? (
          <div className="mt-4 flex items-start gap-2 rounded-radius border border-amber-500 bg-amber-50 p-3 text-amber-950">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-sm">{t(validationMessage)}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default StepSelectYear;
